(() => {
  if (window.__SJSPlayerRuntime) return;
  window.__SJSPlayerRuntime = true;

  const STATE_KEY = 'sjs:audio-player:v1';
  const PJAX_SELECTOR = '#MainContent';
  let activeAudio = null;

  function isShopifyEditor() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function readState() {
    try { return JSON.parse(localStorage.getItem(STATE_KEY) || '{}'); }
    catch { return {}; }
  }

  function writeState(patch) {
    try {
      const state = { ...readState(), ...patch };
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch {}
  }

  function formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  function clampNumber(value, min, max, fallback) {
    const number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(max, Math.max(min, number));
  }

  function fileNameFromUrl(url, fallback = 'sjs-download') {
    try {
      const parsed = new URL(url, window.location.href);
      const last = decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() || '');
      return last || fallback;
    } catch {
      return fallback;
    }
  }

  async function forceBrowserDownload(anchor) {
    const href = anchor.href;
    const requestedName = anchor.dataset.downloadFilename || anchor.getAttribute('download') || fileNameFromUrl(href);

    anchor.classList.add('is-downloading');
    try {
      const response = await fetch(href, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = requestedName || fileNameFromUrl(href);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      // If a CDN blocks CORS blob downloads, fall back to the normal URL.
      window.location.href = href;
    } finally {
      anchor.classList.remove('is-downloading');
    }
  }

  function initForcedDownloads() {
    if (window.__SJSForcedDownloadsBound) return;
    window.__SJSForcedDownloadsBound = true;

    document.addEventListener('click', event => {
      const anchor = event.target.closest && event.target.closest('a[data-force-download]');
      if (!anchor || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      forceBrowserDownload(anchor);
    });
  }

  function initAudioPlayer(root) {
    if (!root || root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    const audio = root.querySelector('[data-audio-element]');
    if (audio && audio.crossOrigin !== 'anonymous') {
      audio.crossOrigin = 'anonymous';
    }
    const buttons = Array.from(root.querySelectorAll('[data-audio-track]'));
    const current = root.querySelector('[data-audio-current]');
    const time = root.querySelector('[data-audio-time]');
    const fill = root.querySelector('[data-audio-meter]');
    const playToggle = root.querySelector('[data-audio-toggle]');
    const seekbar = root.querySelector('[data-audio-seekbar]');
    const volumeBar = root.querySelector('[data-audio-volume]');
    const volumeFill = root.querySelector('[data-audio-volume-fill]');
    const defaultVolume = clampNumber(root.dataset.defaultVolume || 0.7, 0, 1, 0.7);
    const volumeBoost = clampNumber(root.dataset.volumeBoost || 1, 1, 4, 1);
    let currentVolume = defaultVolume;
    let audioGraph = null;
    const emptyText = root.dataset.emptyText || 'Upload WAV in Shopify Files and paste the URL in Theme settings.';
    const tracks = buttons.map((button, index) => ({
      index,
      button,
      src: button.dataset.audioSrc || '',
      label: button.dataset.audioLabel || button.textContent.trim() || `TRACK-${index + 1}.WAV`,
    }));

    function activeTrackIndex() {
      const activeIndex = buttons.findIndex(button => button.classList.contains('active'));
      return activeIndex >= 0 ? activeIndex : 0;
    }

    function setPlayButton(state) {
      if (!playToggle) return;
      playToggle.textContent = state;
      const normalized = String(state).toUpperCase();
      playToggle.setAttribute('aria-label', normalized.includes('PLAY') ? 'Play audio' : normalized.includes('PAUSE') ? 'Pause audio' : 'Audio unavailable');
    }

    function applyOutputVolume() {
      const graph = ensureAudioGraph();
      if (graph) {
        audio.volume = 1;
        graph.gain.gain.value = currentVolume * volumeBoost;
      } else {
        audio.volume = currentVolume;
      }
    }

    function ensureAudioGraph() {
      if (audioGraph !== null) return audioGraph;
      if (volumeBoost <= 1) {
        audioGraph = false;
        return false;
      }

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        audioGraph = false;
        return false;
      }

      try {
        const context = new AudioContextClass();
        const source = context.createMediaElementSource(audio);
        const gain = context.createGain();
        source.connect(gain);
        gain.connect(context.destination);
        audioGraph = { context, gain };
        applyOutputVolume();
      } catch {
        audioGraph = false;
      }
      return audioGraph;
    }

    function resumeAudioGraph() {
      const graph = ensureAudioGraph();
      if (graph && graph.context.state === 'suspended') {
        graph.context.resume().catch(() => {});
      }
    }

    function setVolume(value, persist = true) {
      currentVolume = clampNumber(value, 0, 1, defaultVolume);
      applyOutputVolume();
      const percent = Math.round(currentVolume * 100);
      if (volumeFill) volumeFill.style.width = `${percent}%`;
      if (volumeBar) {
        volumeBar.setAttribute('aria-valuenow', String(percent));
        volumeBar.setAttribute('aria-valuetext', volumeBoost > 1 ? `${percent}% ×${volumeBoost.toFixed(1)} boost` : `${percent}%`);
      }
      if (persist) writeState({ volume: currentVolume });
    }

    function volumeFromPointerEvent(event) {
      if (!volumeBar) return;
      const rect = volumeBar.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      setVolume(ratio);
    }

    function updateProgress() {
      const currentTime = audio.currentTime || 0;
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      if (time) time.textContent = formatTime(currentTime);
      if (fill) {
        const ratio = duration ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
        fill.style.width = `${ratio}%`;
      }
      if (seekbar) {
        seekbar.setAttribute('aria-valuemax', String(Math.floor(duration || 0)));
        seekbar.setAttribute('aria-valuenow', String(Math.floor(currentTime || 0)));
        seekbar.setAttribute('aria-valuetext', `${formatTime(currentTime)} / ${formatTime(duration)}`);
      }
    }

    function seekFromPointerEvent(event) {
      if (!seekbar || !audio.src || !Number.isFinite(audio.duration) || !audio.duration) return;
      const rect = seekbar.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      audio.currentTime = ratio * audio.duration;
      updateProgress();
      writeState({ currentTime: audio.currentTime || 0 });
    }

    function setActive(index, shouldPlay = false, seekTo = null) {
      const track = tracks[index] || tracks[0];
      if (!track) return;

      buttons.forEach((button, buttonIndex) => button.classList.toggle('active', buttonIndex === track.index));
      if (current) current.textContent = track.src ? track.label : emptyText;

      if (!track.src) {
        audio.removeAttribute('src');
        audio.load();
        audio.disabled = true;
        if (playToggle) playToggle.disabled = true;
        setPlayButton('■');
        updateProgress();
        writeState({ trackIndex: track.index, playing: false, currentTime: 0 });
        return;
      }

      audio.disabled = false;
      if (playToggle) playToggle.disabled = false;
      if (audio.crossOrigin !== 'anonymous') {
        audio.crossOrigin = 'anonymous';
      }
      if (audio.src !== track.src) {
        audio.src = track.src;
        audio.load();
      }

      writeState({ trackIndex: track.index, label: track.label });

      const applySeek = () => {
        if (seekTo !== null && Number.isFinite(seekTo) && seekTo > 0 && audio.duration && seekTo < audio.duration) {
          audio.currentTime = seekTo;
        }
      };

      if (audio.readyState >= 1) applySeek();
      else audio.addEventListener('loadedmetadata', applySeek, { once: true });

      if (shouldPlay) {
        resumeAudioGraph();
        audio.play().catch(() => {
          writeState({ playing: false });
        });
      }
    }

    buttons.forEach((button, index) => {
      if (!tracks[index].src) {
        button.classList.add('disabled');
        button.setAttribute('aria-disabled', 'true');
      }

      button.addEventListener('click', () => {
        const sameTrack = audio.src === tracks[index].src && !audio.paused;
        if (sameTrack) {
          audio.pause();
          writeState({ playing: false });
          return;
        }
        setActive(index, Boolean(tracks[index].src), 0);
      });
    });

    if (playToggle) {
      playToggle.addEventListener('click', () => {
        const index = activeTrackIndex();
        const track = tracks[index] || tracks[0];
        if (!track || !track.src) return;
        if (!audio.src) {
          setActive(index, true, readState().currentTime || 0);
          return;
        }
        if (audio.paused) {
          resumeAudioGraph();
          audio.play().catch(() => writeState({ playing: false }));
        } else {
          audio.pause();
        }
      });
    }

    if (seekbar) {
      let dragging = false;
      seekbar.addEventListener('pointerdown', event => {
        dragging = true;
        seekbar.setPointerCapture?.(event.pointerId);
        seekFromPointerEvent(event);
      });
      seekbar.addEventListener('pointermove', event => {
        if (dragging) seekFromPointerEvent(event);
      });
      seekbar.addEventListener('pointerup', event => {
        dragging = false;
        seekbar.releasePointerCapture?.(event.pointerId);
      });
      seekbar.addEventListener('keydown', event => {
        if (!audio.src || !Number.isFinite(audio.duration) || !audio.duration) return;
        const step = event.shiftKey ? 10 : 5;
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
          event.preventDefault();
          audio.currentTime = Math.min(audio.duration, (audio.currentTime || 0) + step);
          updateProgress();
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
          event.preventDefault();
          audio.currentTime = Math.max(0, (audio.currentTime || 0) - step);
          updateProgress();
        } else if (event.key === 'Home') {
          event.preventDefault();
          audio.currentTime = 0;
          updateProgress();
        } else if (event.key === 'End') {
          event.preventDefault();
          audio.currentTime = audio.duration;
          updateProgress();
        }
      });
    }

    if (volumeBar) {
      let volumeDragging = false;
      volumeBar.addEventListener('pointerdown', event => {
        volumeDragging = true;
        volumeBar.setPointerCapture?.(event.pointerId);
        volumeFromPointerEvent(event);
      });
      volumeBar.addEventListener('pointermove', event => {
        if (volumeDragging) volumeFromPointerEvent(event);
      });
      volumeBar.addEventListener('pointerup', event => {
        volumeDragging = false;
        volumeBar.releasePointerCapture?.(event.pointerId);
      });
      volumeBar.addEventListener('keydown', event => {
        const step = event.shiftKey ? 0.1 : 0.05;
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
          event.preventDefault();
          setVolume(currentVolume + step);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
          event.preventDefault();
          setVolume(currentVolume - step);
        } else if (event.key === 'Home') {
          event.preventDefault();
          setVolume(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          setVolume(1);
        }
      });
    }

    audio.addEventListener('play', () => {
      if (activeAudio && activeAudio !== audio) activeAudio.pause();
      activeAudio = audio;
      root.classList.add('is-playing');
      setPlayButton('Ⅱ PAUSE');
      writeState({ playing: true, trackIndex: activeTrackIndex() });
    });

    audio.addEventListener('pause', () => {
      root.classList.remove('is-playing');
      setPlayButton('► PLAY');
      writeState({ playing: false, currentTime: audio.currentTime || 0 });
    });

    audio.addEventListener('ended', () => {
      root.classList.remove('is-playing');
      setPlayButton('► PLAY');
      writeState({ playing: false, currentTime: 0 });
    });

    audio.addEventListener('timeupdate', () => {
      updateProgress();
      writeState({ currentTime: audio.currentTime || 0 });
    });

    audio.addEventListener('loadedmetadata', updateProgress);

    const state = readState();
    const savedVolume = Number(state.volume);
    setVolume(Number.isFinite(savedVolume) ? savedVolume : defaultVolume, false);
    const restoredIndex = Number.isInteger(state.trackIndex) ? state.trackIndex : 0;
    setActive(restoredIndex, false, Number(state.currentTime || 0));
  }

  function initAudioPlayers() {
    document.querySelectorAll('[data-sjs-audio-player]').forEach(initAudioPlayer);
  }

  function closeVideoModal(modal) {
    if (!modal) return;
    const video = modal.querySelector('[data-sjs-video]');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.removeAttribute('poster');
      video.load();
    }
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sjs-video-open');
  }

  function openVideoModal(button) {
    const src = button.dataset.videoSrc || '';
    if (!src) return;

    const section = button.closest('.sjs-digital-downloads-section') || document;
    const modal = section.querySelector('[data-sjs-video-modal]') || document.querySelector('[data-sjs-video-modal]');
    if (!modal) return;

    const video = modal.querySelector('[data-sjs-video]');
    const title = modal.querySelector('[data-sjs-video-title]');
    const meta = modal.querySelector('[data-sjs-video-meta]');
    const download = modal.querySelector('[data-sjs-video-download]');
    if (!video) return;

    if (activeAudio) activeAudio.pause();
    video.pause();
    video.src = src;
    if (button.dataset.videoPoster) video.poster = button.dataset.videoPoster;
    else video.removeAttribute('poster');
    if (title) title.textContent = button.dataset.videoTitle || 'QuickTime Player 3.0';
    if (meta) meta.textContent = button.dataset.videoMeta || 'QuickTime video';
    if (download) {
      download.href = button.dataset.videoDownload || src;
      download.textContent = button.dataset.videoDownloadLabel || '↓ download file';
    }

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sjs-video-open');
    video.load();

    if (modal.dataset.autoplay === 'true') {
      video.play().catch(() => {});
    }
  }

  function initVideoModals() {
    if (window.__SJSVideoModalsBound) return;
    window.__SJSVideoModalsBound = true;

    document.addEventListener('click', event => {
      const openButton = event.target.closest && event.target.closest('[data-sjs-video-open]');
      if (openButton) {
        event.preventDefault();
        openVideoModal(openButton);
        return;
      }

      const closeButton = event.target.closest && event.target.closest('[data-sjs-video-close]');
      if (closeButton) {
        event.preventDefault();
        closeVideoModal(closeButton.closest('[data-sjs-video-modal]'));
      }
    });

    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-sjs-video-modal]:not([hidden])').forEach(closeVideoModal);
    });
  }

  function isPjaxEligible(anchor, event) {
    if (!anchor || event.defaultPrevented) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (anchor.target && anchor.target !== '_self') return false;
    if (anchor.hasAttribute('download') || anchor.dataset.noPjax === 'true') return false;
    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return false;

    let url;
    try { url = new URL(href, window.location.href); }
    catch { return false; }

    if (url.origin !== window.location.origin) return false;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return false;

    const blockedPrefixes = ['/cart', '/checkout', '/account', '/admin', '/password', '/challenge', '/apps'];
    if (blockedPrefixes.some(prefix => url.pathname.startsWith(prefix))) return false;
    if (url.pathname.match(/\.(zip|pdf|mp3|wav|mov|mp4|jpg|jpeg|png|gif|webp)$/i)) return false;

    return true;
  }

  async function loadPage(url, push = true) {
    document.documentElement.classList.add('sjs-pjax-loading');
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextMain = doc.querySelector(PJAX_SELECTOR);
      const currentMain = document.querySelector(PJAX_SELECTOR);
      if (!nextMain || !currentMain) throw new Error('Main content missing');

      const replacements = [
        ['nav.topnav', 'nav.topnav'],
        ['aside.col-left', 'aside.col-left'],
        ['.statusbar', '.statusbar'],
      ];
      replacements.forEach(([currentSelector, nextSelector]) => {
        const currentNode = document.querySelector(currentSelector);
        const nextNode = doc.querySelector(nextSelector);
        if (currentNode && nextNode) currentNode.replaceWith(nextNode);
      });

      currentMain.replaceWith(nextMain);
      document.title = doc.title || document.title;
      document.body.className = doc.body.className;
      const nextPage = doc.querySelector('.page');
      const currentPage = document.querySelector('.page');
      if (nextPage && currentPage) currentPage.dataset.screenLabel = nextPage.dataset.screenLabel || currentPage.dataset.screenLabel;

      if (push) history.pushState({ sjsPjax: true }, '', url);
      window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
      document.dispatchEvent(new CustomEvent('sjs:page-load', { detail: { url } }));
    } catch (error) {
      window.location.href = url;
    } finally {
      document.documentElement.classList.remove('sjs-pjax-loading');
    }
  }

  function initPjax() {
    if (isShopifyEditor()) return;

    document.addEventListener('click', event => {
      const anchor = event.target.closest && event.target.closest('a[href]');
      if (!isPjaxEligible(anchor, event)) return;
      event.preventDefault();
      const url = new URL(anchor.href);
      loadPage(url.href, true);
    });

    window.addEventListener('popstate', () => {
      loadPage(window.location.href, false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAudioPlayers();
      initForcedDownloads();
      initVideoModals();
      initPjax();
    }, { once: true });
  } else {
    initAudioPlayers();
    initForcedDownloads();
    initVideoModals();
    initPjax();
  }

  document.addEventListener('shopify:section:load', initAudioPlayers);
  document.addEventListener('sjs:page-load', initAudioPlayers);
})();
